import {
  DEBUG_SDK_DEFAULT_URL,
  DEBUG_SDK_LOGS_SS_KEY,
  DEBUG_SDK_LS_KEY,
  DEBUG_SDK_QUERY_PARAM,
  DEBUG_SDK_SCRIPT_MARKER,
  DEBUG_SDK_SESSION_LS_KEY,
  DEBUG_SDK_UI_LS_KEY,
} from "../config";
import { debugBus } from "./bus";
import { clearUrlState } from "./urlState";

export interface DebugSdkActivationOptions {
  debugSdk?: boolean;
  debugSdkUrl?: string;
}

const readLocalStorageFlag = (): boolean => {
  try {
    return localStorage.getItem(DEBUG_SDK_LS_KEY) === "1";
  } catch {
    return false;
  }
};

const readQueryFlag = (): boolean => {
  try {
    return (
      new URLSearchParams(window.location.search).get(DEBUG_SDK_QUERY_PARAM) ===
      "true"
    );
  } catch {
    return false;
  }
};

const mirrorActivationToLocalStorage = (): void => {
  try {
    if (localStorage.getItem(DEBUG_SDK_LS_KEY) !== "1") {
      localStorage.setItem(DEBUG_SDK_LS_KEY, "1");
    }
  } catch {
    // privacy / quota — fall through; URL still keeps the flag.
  }
};

/**
 * Activate the optional debug SDK if any of the three opt-in signals is set:
 *   - `opts.debugSdk === true`               (config flag)
 *   - `localStorage['dqtag:debugSdk'] === '1'`
 *   - `?dqtag_debugsdk=true`                 (URL query param)
 *
 * Whichever signal fired, mirror it into localStorage so subsequent hard
 * navigations (where the URL might be lost) keep the debugger live until the
 * publisher hits Exit & close.
 *
 * Safe to call multiple times; the script tag is only injected once.
 * NOOP outside of a browser environment.
 */
export function maybeActivateDebugSdk(
  opts: DebugSdkActivationOptions = {},
): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  const enabled =
    opts.debugSdk === true || readLocalStorageFlag() || readQueryFlag();
  if (!enabled) {
    return;
  }

  mirrorActivationToLocalStorage();
  debugBus.enable();

  if (document.querySelector(`script[${DEBUG_SDK_SCRIPT_MARKER}]`)) {
    return;
  }

  const script = document.createElement("script");
  script.src = opts.debugSdkUrl ?? DEBUG_SDK_DEFAULT_URL;
  script.async = true;
  script.setAttribute(DEBUG_SDK_SCRIPT_MARKER, "1");
  (document.head ?? document.documentElement).appendChild(script);
}

/**
 * Tear down all debugger state: localStorage keys, sessionStorage logs,
 * URL params, injected <script> tag, and the in-memory bus. Used by the
 * Exit & close button. Safe to call multiple times.
 */
export function cleanupActivation(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  clearUrlState();

  try {
    localStorage.removeItem(DEBUG_SDK_LS_KEY);
    localStorage.removeItem(DEBUG_SDK_UI_LS_KEY);
    localStorage.removeItem(DEBUG_SDK_SESSION_LS_KEY);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(DEBUG_SDK_LOGS_SS_KEY);
  } catch {
    // ignore
  }

  const script = document.querySelector(`script[${DEBUG_SDK_SCRIPT_MARKER}]`);
  script?.parentNode?.removeChild(script);

  debugBus.disable();
}
